/**
 * Gradient tick checkbox — CSS checkbox-wrapper motion with theme colors.
 * Variants: shape (circle | square), tone (brand | neutral), configurable square radius.
 * Self-contained; no new deps (RN + Reanimated + react-native-svg).
 */
import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Pressable, StyleSheet, View, type ViewStyle } from 'react-native';
import Animated, {
  Easing,
  Extrapolation,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { triggerHaptic } from '../../shared/utils/haptics';

const DEFAULT_SIZE = 50;
const ANIM_MS = 200;
const EASE = Easing.ease;
/** Default square corner radius as a fraction of size. */
const DEFAULT_SQUARE_RADIUS_RATIO = 0.22;
/** Compact size for auth / form rows. */
export const GRADIENT_TICK_CHECKBOX_FORM_SIZE = 24;

export type GradientTickCheckboxShape = 'circle' | 'square';
export type GradientTickCheckboxTone = 'brand' | 'neutral';

/** Map checkbox fills/shadows onto theme tokens. */
function resolveCheckboxPalette(c: ThemeColors) {
  return {
    gradient_start: c.primary,
    gradient_mid: c.primary,
    gradient_end: c.primary,
    inner_fill: c.white,
    tick_fill: c.primaryText,
    soft_shadow: c.primary,
    depth_shadow: c.shadowColor,
    /** Neutral unselected fill */
    neutral_fill: c.black,
    neutral_soft_shadow: c.greyDark,
  };
}

export type GradientTickCheckboxProps = {
  value?: boolean;
  default_value?: boolean;
  onValueChange?: (value: boolean) => void;
  disabled?: boolean;
  /** Outer edge length. Default 50. */
  size?: number;
  /** `circle` (default) or `square`. */
  shape?: GradientTickCheckboxShape;
  /**
   * Corner radius for `shape="square"` only.
   * Defaults to ~22% of `size`. Ignored for circle (always fully round).
   */
  borderRadius?: number;
  /**
   * `brand` (default): gradient always under the white inset.
   * `neutral`: black when unchecked; brand gradient + tick when checked.
   */
  tone?: GradientTickCheckboxTone;
  /** Validation ring — soft error tint when unchecked. */
  error?: boolean;
  /** Light selection haptic on toggle (iOS + softened Android). Default true. */
  haptics?: boolean;
  testID?: string;
  style?: ViewStyle;
  accessibilityLabel?: string;
};

type Layout = {
  size: number;
  outer_radius: number;
  shadow_y: number;
  inner: number;
  inner_hover: number;
  /** Tick L geometry (local coords, y-down). */
  tick_w: number;
  tick_h: number;
  tick_stem_w: number;
  tick_stem_h: number;
  tick_bar_h: number;
  /** Area-weighted ink centroid (local top-left origin). */
  tick_cx: number;
  tick_cy: number;
  tick_stem_hide_y: number;
  tick_bar_hide_x: number;
};

/**
 * Tight L tick sized for optical balance. Positioned so its area centroid
 * sits on the control center; rotation uses that centroid as origin.
 */
function buildLayout(size: number, shape: GradientTickCheckboxShape, border_radius?: number): Layout {
  const outer_radius =
    shape === 'circle' ? size / 2 : (border_radius ?? size * DEFAULT_SQUARE_RADIUS_RATIO);

  // Balanced check proportions (short leg / long leg / stroke).
  const stem_w = size * 0.095;
  const stem_h = size * 0.28;
  const bar_h = size * 0.095;
  const bar_w = size * 0.46;
  const tick_w = bar_w;
  const tick_h = stem_h;

  // Area-weighted centroid of the L (stem ∪ bar), local top-left origin.
  const a_stem = stem_w * stem_h;
  const a_bar = bar_w * bar_h;
  const a_overlap = stem_w * bar_h;
  const a_union = a_stem + a_bar - a_overlap;

  const cx_stem = stem_w / 2;
  const cy_stem = stem_h / 2;
  const cx_bar = bar_w / 2;
  const cy_bar = stem_h - bar_h / 2;
  const cx_overlap = stem_w / 2;
  const cy_overlap = stem_h - bar_h / 2;

  const tick_cx = (cx_stem * a_stem + cx_bar * a_bar - cx_overlap * a_overlap) / a_union;
  const tick_cy = (cy_stem * a_stem + cy_bar * a_bar - cy_overlap * a_overlap) / a_union;

  return {
    size,
    outer_radius: Math.max(0, outer_radius),
    shadow_y: size * 0.1,
    inner: size * 0.85,
    inner_hover: size * 0.55,
    tick_w,
    tick_h,
    tick_stem_w: stem_w,
    tick_stem_h: stem_h,
    tick_bar_h: bar_h,
    tick_cx,
    tick_cy,
    tick_stem_hide_y: -(stem_h + size * 0.35),
    tick_bar_hide_x: bar_w + size * 0.25,
  };
}

/** Inset corner radius scaled with the shrinking inner face. */
function innerCornerRadius(outer_radius: number, outer_size: number, inner_size: number): number {
  'worklet';
  if (outer_size <= 0) {
    return 0;
  }
  return Math.max(0, outer_radius * (inner_size / outer_size));
}

export function GradientTickCheckbox({
  value,
  default_value = false,
  onValueChange,
  disabled = false,
  size = DEFAULT_SIZE,
  shape = 'circle',
  borderRadius,
  tone = 'brand',
  error = false,
  haptics = true,
  testID,
  style,
  accessibilityLabel,
}: GradientTickCheckboxProps) {
  const is_controlled = value !== undefined;
  const [internal_value, set_internal_value] = useState(default_value);
  const checked = is_controlled ? Boolean(value) : internal_value;

  const colors = useThemeColors() ?? appTheme.colors;
  const palette = useMemo(() => resolveCheckboxPalette(colors), [colors]);
  const layout = useMemo(() => buildLayout(size, shape, borderRadius), [size, shape, borderRadius]);
  const grad_id = useId().replace(/:/g, '');
  const is_neutral = tone === 'neutral';

  const checked_sv = useSharedValue(checked ? 1 : 0);
  const hover_sv = useSharedValue(0);
  const press_sv = useSharedValue(0);

  useEffect(() => {
    checked_sv.value = withTiming(checked ? 1 : 0, { duration: ANIM_MS, easing: EASE });
  }, [checked, checked_sv]);

  const on_toggle = useCallback(() => {
    if (disabled) {
      return;
    }
    const next = !checked;
    if (!is_controlled) {
      set_internal_value(next);
    }
    if (haptics) {
      triggerHaptic('selection');
    }
    onValueChange?.(next);
  }, [checked, disabled, is_controlled, onValueChange, haptics]);

  const on_press_in = useCallback(() => {
    if (disabled) {
      return;
    }
    hover_sv.value = withTiming(1, { duration: ANIM_MS, easing: EASE });
    press_sv.value = withTiming(1, { duration: ANIM_MS, easing: EASE });
  }, [disabled, hover_sv, press_sv]);

  const on_press_out = useCallback(() => {
    hover_sv.value = withTiming(0, { duration: ANIM_MS, easing: EASE });
    press_sv.value = withTiming(0, { duration: ANIM_MS, easing: EASE });
  }, [hover_sv, press_sv]);

  const label_style = useAnimatedStyle(() => {
    const pressed = press_sv.value;
    const is_on = checked_sv.value;
    return {
      transform: [{ scale: interpolate(pressed, [0, 1], [1, 0.9]) }],
      shadowOpacity: interpolate(is_on, [0, 1], [0.55, 0.35]),
      shadowRadius: interpolate(is_on, [0, 1], [layout.shadow_y, 19]),
      shadowOffset: {
        width: 0,
        height: interpolate(is_on, [0, 1], [layout.shadow_y, 15]),
      },
      elevation: interpolate(is_on, [0, 1], [4, 10]),
    };
  });

  const gradient_layer_style = useAnimatedStyle(() => {
    // Brand: gradient always visible. Neutral: fades in on check.
    if (!is_neutral) {
      return { opacity: 1 };
    }
    return { opacity: checked_sv.value };
  });

  const neutral_layer_style = useAnimatedStyle(() => {
    if (!is_neutral) {
      return { opacity: 0 };
    }
    return { opacity: interpolate(checked_sv.value, [0, 1], [1, 0]) };
  });

  const inner_style = useAnimatedStyle(() => {
    const is_on = checked_sv.value;
    const hover = hover_sv.value;
    const base = interpolate(hover, [0, 1], [layout.inner, layout.inner_hover]);
    const edge = interpolate(is_on, [0, 1], [base, 0], Extrapolation.CLAMP);
    return {
      width: edge,
      height: edge,
      borderRadius: innerCornerRadius(layout.outer_radius, layout.size, edge),
      opacity: interpolate(is_on, [0, 0.85, 1], [1, 1, 0]),
    };
  });

  const stem_style = useAnimatedStyle(() => {
    const p = checked_sv.value;
    return {
      opacity: p,
      transform: [{ translateY: interpolate(p, [0, 1], [layout.tick_stem_hide_y, 0]) }],
    };
  });

  const bar_style = useAnimatedStyle(() => {
    const p = checked_sv.value;
    return {
      opacity: p,
      transform: [{ translateX: interpolate(p, [0, 1], [layout.tick_bar_hide_x, 0]) }],
    };
  });

  const idle_shadow = error && !checked ? colors.error : is_neutral ? palette.neutral_soft_shadow : palette.soft_shadow;

  return (
    <Pressable
      testID={testID}
      onPress={on_toggle}
      onPressIn={on_press_in}
      onPressOut={on_press_out}
      disabled={disabled}
      accessibilityRole="checkbox"
      accessibilityState={{ checked, disabled }}
      accessibilityLabel={accessibilityLabel}
      style={style}>
      <Animated.View
        style={[
          styles.label,
          {
            width: layout.size,
            height: layout.size,
            borderRadius: layout.outer_radius,
            opacity: disabled ? 0.55 : 1,
            shadowColor: checked ? palette.depth_shadow : idle_shadow,
            borderWidth: error && !checked ? StyleSheet.hairlineWidth * 2 : 0,
            borderColor: error && !checked ? colors.error : 'transparent',
          },
          label_style,
        ]}>
        {/* Neutral black fill (under gradient; visible only for tone=neutral when unchecked) */}
        <Animated.View
          pointerEvents="none"
          style={[
            StyleSheet.absoluteFill,
            {
              backgroundColor: palette.neutral_fill,
              borderRadius: layout.outer_radius,
            },
            neutral_layer_style,
          ]}
        />

        {/* Brand gradient */}
        <Animated.View pointerEvents="none" style={[StyleSheet.absoluteFill, gradient_layer_style]}>
          <Svg width={layout.size} height={layout.size}>
            <Defs>
              <LinearGradient id={`${grad_id}-bg`} x1="0" y1="1" x2="1" y2="0">
                <Stop offset="0" stopColor={palette.gradient_start} />
                <Stop offset="0.46" stopColor={palette.gradient_mid} />
                <Stop offset="1" stopColor={palette.gradient_end} />
              </LinearGradient>
            </Defs>
            <Rect
              x={0}
              y={0}
              width={layout.size}
              height={layout.size}
              rx={layout.outer_radius}
              ry={layout.outer_radius}
              fill={`url(#${grad_id}-bg)`}
            />
          </Svg>
        </Animated.View>

        {/* Inner inset face */}
        <Animated.View
          pointerEvents="none"
          style={[
            styles.inner,
            {
              backgroundColor: palette.inner_fill,
              shadowColor: idle_shadow,
              shadowOpacity: 1,
              shadowRadius: 2,
              shadowOffset: { width: 0, height: layout.shadow_y * 0.5 },
            },
            inner_style,
          ]}
        />

        {/*
          Place the L so its area centroid is on the control center, then rotate
          about that centroid — identical for circle and square.
        */}
        <View pointerEvents="none" style={styles.tick_host}>
          <View
            style={{
              position: 'absolute',
              left: layout.size / 2 - layout.tick_cx,
              top: layout.size / 2 - layout.tick_cy,
              width: layout.tick_w,
              height: layout.tick_h,
              transformOrigin: [layout.tick_cx, layout.tick_cy, 0],
              transform: [{ rotateZ: '-45deg' }],
            }}>
            <Animated.View
              style={[
                styles.tick_stem,
                {
                  width: layout.tick_stem_w,
                  height: layout.tick_stem_h,
                  borderRadius: Math.max(1, layout.tick_stem_w * 0.35),
                  backgroundColor: palette.tick_fill,
                },
                stem_style,
              ]}
            />
            <Animated.View
              style={[
                styles.tick_bar,
                {
                  width: layout.tick_w,
                  height: layout.tick_bar_h,
                  borderRadius: Math.max(1, layout.tick_bar_h * 0.35),
                  backgroundColor: palette.tick_fill,
                },
                bar_style,
              ]}
            />
          </View>
        </View>
      </Animated.View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  label: {
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  inner: {
    position: 'absolute',
  },
  tick_host: {
    ...StyleSheet.absoluteFillObject,
  },
  tick_stem: {
    position: 'absolute',
    left: 0,
    bottom: 0,
  },
  tick_bar: {
    position: 'absolute',
    left: 0,
    bottom: 0,
  },
});

export default GradientTickCheckbox;
